import * as _ from 'lodash';
import { ITableConfig } from '../components/common/table';

export enum ODataOrderByDirection {
    Ascending = 1,
    Descending,
}

export class ODataOrderBy {
    private readonly propety: string;
    private readonly direction: ODataOrderByDirection;

    constructor(property: string, direction: ODataOrderByDirection) {
        this.propety = property;
        this.direction = direction;
    }

    public getProperty() {
        return this.propety;
    }

    public getDirection() {
        return this.direction;
    }

    public toString() {
        if (this.direction === ODataOrderByDirection.Ascending) {
            return this.propety;
        } else {
            return this.propety + ' desc';
        }
    }
}

// export class ODataOmniSearch {
//     private readonly searchFields: Array<String>;
//     private readonly searchValue: string;

//     constructor(searchFields: Array<String>, searchValue: string) {
//         this.searchFields = searchFields;
//         this.searchValue = searchValue;
//     }

//     public getSearchFields() {
//         return this.searchFields;
//     }

//     public getSearchValue() {
//         return this.searchValue;
//     }

//     public toString() {
//         return this.searchFields.toString() + ':' + this.searchValue;
//     }
// }

export enum ODataFilterOperators {
    Equals = 1,
    NotEquals,
    GreaterThan,
    GreaterThanOrEqual,
    LessThan,
    LessThanOrEqual,
    Between,
    Contains,
    StartsWith,
    EndsWith,
}

export class ODataFilter {
    private readonly property: string;
    private readonly filterOperation: ODataFilterOperators;
    // tslint:disable-next-line
    private readonly value1: any;
    // tslint:disable-next-line
    private readonly value2: any;

    // tslint:disable-next-line
    constructor(property: string, filterOperation: ODataFilterOperators, value: any, secondValue?: any) {
        this.property = property;
        this.filterOperation = filterOperation;
        this.value1 = value;
        this.value2 = secondValue;
    }

    public toString() {
        switch (this.filterOperation) {
            case ODataFilterOperators.Equals:
                return `${this.property} eq ${this.value1}`;
            case ODataFilterOperators.NotEquals:
                return `${this.property} ne ${this.value1}`;
            case ODataFilterOperators.GreaterThan:
                return `${this.property} gt ${this.value1}`;
            case ODataFilterOperators.GreaterThanOrEqual:
                return `${this.property} ge ${this.value1}`;
            case ODataFilterOperators.LessThan:
                return `${this.property} lt ${this.value1}`;
            case ODataFilterOperators.LessThanOrEqual:
                return `${this.property} le ${this.value1}`;
            case ODataFilterOperators.Between:
                // Between typically means value1 <= property <= value2
                return `(${this.property} ge ${this.value1} and ${this.property} le ${this.value2})`;
            case ODataFilterOperators.Contains:
                return `contains(${this.property}, '${this.value1}')`;
            case ODataFilterOperators.StartsWith:
                return `startswith(${this.property}, '${this.value1}')`;
            case ODataFilterOperators.EndsWith:
                return `endswith(${this.property}, '${this.value1}')`;
            default:
                return '';
        }
    }
}

export class ODataOptions {
    private readonly filters: ODataFilter[];
    private pageSize: number;
    private pageNumber: number;
    private orderby: ODataOrderBy;
    // private omniSearch: ODataOmniSearch;
    private selects: string[];
    private skip: number;
    private top: number;
    private nextPage: boolean;

    static from(config: ITableConfig) {
        const options = new ODataOptions();
        options.pageNumber = config.pageNumber;
        options.pageSize = config.rowCount;

        return options;
    }

    constructor() {
        this.filters = new Array<ODataFilter>();
        this.selects = new Array<string>();
        this.pageSize = 0;
        this.pageNumber = 0;
        this.nextPage = false;
    }

    public merge(options: ODataOptions) {
        if (!options) {
            return this;
        }

        if (options.orderby) {
            this.addOrderBy(options.orderby.getProperty(), options.orderby.getDirection());
        }

        // if (options.omniSearch) {
        //     this.addOmniSearch(options.omniSearch.getSearchFields(), options.omniSearch.getSearchValue());
        // }

        if (options?.filters?.length > 0) {
            for (let i = 0; i < options.filters.length; i++) {
                const filter = options.filters[i];
                this.addFilter(filter);
            }
        }

        return this;
    }

    public addFilter(filter: ODataFilter) {
        this.filters.push(filter);
        return this;
    }

    public getFilters() {
        return this.filters;
    }

    public setFilters(newFilters: Array<ODataFilter>) {
        if (!!newFilters && newFilters.length > 0) {
            this.clearFilters();
            for (let filterIndex = 0; filterIndex < newFilters.length; filterIndex++) {
                this.addFilter(newFilters[filterIndex]);
            }
        }
    }

    public clearFilters(field: string = '') {
        if (field) {
            let removeIndices = new Array<number>();
            for (let filterIndex = 0; filterIndex < this.filters.length; filterIndex++) {
                let tempString = this.filters[filterIndex].toString();
                if (tempString.search(field) > -1) {
                    removeIndices.push(filterIndex);
                }
            }
            for (let deleteIndex = removeIndices.length - 1; deleteIndex >= 0; deleteIndex--) {
                this.filters.splice(removeIndices[deleteIndex], 1);
            }
        } else {            
            this.filters.length = 0;
        }
    }

    public hasFilters() {
        return this.filters.length > 0 ? true : false;
    }

    public addPaging(pageNumber: number, pageSize: number) {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;
        return this;
    }

    public addSkipTop(skip: number, top: number) {
        this.skip = skip;
        this.top = top;
        return this;
    }

    public addOrderBy(property: string, orderby: ODataOrderByDirection) {
        this.orderby = new ODataOrderBy(property, orderby);
        return this;
    }

    // public addOmniSearch(searchFields: Array<String>, searchValue: string) {
    //     this.omniSearch = new ODataOmniSearch(searchFields, searchValue);
    //     return this;
    // }

    public addSelect(properties: string | string[]) {
        this.selects = Array.isArray(properties) ? properties : [properties];
        return this;
    }

    public getPageNumber() {
        return this.pageNumber;
    }

    public incrementPageNumber() {
        this.pageNumber++;
    }

    public getPageSize() {
        return this.pageSize;
    }

    public getIsNextPage() {
        return this.nextPage;
    }

    public setIsNextpage(nextPage: boolean) {
        this.nextPage = nextPage;
    }

    public getOrderByDirection() {
        return this.orderby !== undefined ? this.orderby.getDirection() : undefined;
    }

    public getOrderByProperty() {
        return this.orderby !== undefined ? this.orderby.getProperty() : undefined;
    }

    // tslint:disable-next-line
    public orderCollection(items: any[]) {
        let retItems = [...items];

        if (this.orderby) {
            // tslint:disable-next-line
            retItems = retItems.sort((item1: any, item2: any) => {
                const prop1 = item1[this.orderby.getProperty()];
                const prop2 = item2[this.orderby.getProperty()];

                const mod = this.orderby.getDirection() === ODataOrderByDirection.Ascending ? 1 : -1;

                return prop1 < prop2 ? -1 * mod : prop1 > prop2 ? 1 * mod : 0;
            });
        }

        return retItems.slice((this.pageNumber - 1) * this.pageSize, this.pageNumber * this.pageSize);
    }

    public toString() {
        const odataQuery: string[] = [];

        if (this.selects.length > 0) {
            odataQuery.push(`$select=${this.selects.join(',')}`);
        }

        if (this.filters.length > 0) {
            odataQuery.push(`$filter=${this.filters.map((filter) => {
                return filter.toString();
            }).join('--and--')}`);
        }

        if (this.skip !== undefined && this.top !== undefined) {
            odataQuery.push(`$skip=${this.skip}&$top=${this.top}`);
        } else if (this.pageNumber && this.pageSize) {
            const skip = this.pageSize * (this.pageNumber - 1);
            odataQuery.push(`$skip=${skip}&$top=${this.pageSize}`);
        }

        if (this.orderby) {
            odataQuery.push(`$orderby=${this.orderby.toString()}`);
        }

        // if (this.omniSearch) {
        //     odataQuery.push(`$omniSearch=${this.omniSearch.toString()}`);
        // }

        if (odataQuery.length > 0) {
            const ret = '?' + odataQuery.join('&');
            // console.log('odata query', ret);
            return ret;
        } else {
            return '';
        }
    }
}



// WEBPACK FOOTER //
// ./src/util/ODataBuilder.ts